import React from 'react';
import { useTranslation } from '../../../../shared/i18n';

// Zodiac display component
const ZodiacDisplay = ({ zodiacAnimal, zodiacName }) => {
  const getEmoji = (animal) => {
    const emojis = {
      rat: '🐀',
      buffalo: '🐃',
      tiger: '🐅',
      cat: '🐈',
      dragon: '🐉',
      snake: '🐍',
      horse: '🐴',
      goat: '🐐',
      monkey: '🐒',
      rooster: '🐓',
      dog: '🐕',
      pig: '🐖'
    };
    return emojis[animal] || '🐾';
  };

  return (
    <div
      className="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center mx-auto"
      style={{ width: '120px', height: '120px' }}
    >
      <span style={{ fontSize: '64px' }}>{getEmoji(zodiacAnimal)}</span>
    </div>
  );
};

// Stat card component
const StatCard = ({ icon, label, value, color }) => (
  <div className="card border-0 rounded-4 h-100">
    <div className="card-body p-4">
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <p className="text-secondary small mb-1">{label}</p>
          <h2 className="fs-3 fw-bold mb-0">{value}</h2>
        </div>
        <div
          className={`bg-${color}-subtle rounded-3 d-flex align-items-center justify-content-center`}
          style={{ width: '48px', height: '48px' }}
        >
          {icon}
        </div>
      </div>
    </div>
  </div>
);

// Icons
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#0d6efd" viewBox="0 0 448 512">
    <path d="M128 0c17.7 0 32 14.3 32 32V64H288V32c0-17.7 14.3-32 32-32s32 14.3 32 32V64h48c26.5 0 48 21.5 48 48v48H0V112C0 85.5 21.5 64 48 64H96V32c0-17.7 14.3-32 32-32zM0 192H448V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V192zm64 80v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm128 0v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H208c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H336zM64 400v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H208zm112 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H336c-8.8 0-16 7.2-16 16z"/>
  </svg>
);

const ZodiacIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#6f42c1" viewBox="0 0 512 512">
    <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm50.7-186.9L162.4 380.6c-19.4 7.5-38.5-11.6-31-31l55.5-144.3c3.3-8.5 9.9-15.1 18.4-18.4l144.3-55.5c19.4-7.5 38.5 11.6 31 31L325.1 306.7c-3.2 8.5-9.9 15.1-18.4 18.4zM288 256a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"/>
  </svg>
);

const FireIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" fill="#fd7e14" viewBox="0 0 448 512">
    <path d="M159.3 5.4c7.8-7.3 19.9-7.2 27.7 .1c27.6 25.9 53.5 53.8 77.7 84c11-14.4 23.5-30.1 37-42.9c7.9-7.4 20.1-7.4 28 .1c34.6 33 63.9 76.6 84.5 118c20.3 40.8 33.8 82.5 33.8 111.9C448 404.2 348.2 512 224 512C98.4 512 0 404.1 0 276.5c0-38.4 17.8-85.3 45.4-131.7C73.3 97.7 112.7 48.6 159.3 5.4zM225.7 416c25.3 0 47.7-7 68.8-21c42.1-29.4 53.4-88.2 28.1-134.4c-4.5-9-16-9.6-22.5-2l-25.2 29.3c-6.6 7.6-18.5 7.4-24.7-.5c-16.5-21-46-58.5-62.8-79.8c-6.3-8-18.3-8.1-24.7-.1c-33.8 42.5-50.8 69.3-50.8 99.4C112 375.4 162.6 416 225.7 416z"/>
  </svg>
);

const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" fill="#198754" viewBox="0 0 576 512">
    <path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/>
  </svg>
);

export const Profile = ({ data }) => {
  const { t } = useTranslation();
  const { child, stats } = data;

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <header className="mb-4">
        <h1 className="fs-2 fw-bold mb-1">{t('children.dashboard.profile.title')}</h1>
        <p className="text-secondary mb-0">{t('children.dashboard.profile.subtitle')}</p>
      </header>

      {/* Profile Card */}
      <div className="card border-0 rounded-4 mb-4">
        <div className="card-body p-4 text-center">
          {/* Zodiac Avatar */}
          <div className="mb-3">
            <ZodiacDisplay
              zodiacAnimal={child.zodiac_animal}
              zodiacName={child.zodiac_name}
            />
          </div>

          {/* Name */}
          <h2 className="fw-bold fs-3 mb-1">{child.username}</h2>

          {/* Zodiac Badge */}
          <span
            className="badge rounded-pill px-3 py-2"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            {child.zodiac_name}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="row g-3 mb-4">
        <div className="col-md-6 col-lg-3">
          <StatCard
            icon={<CalendarIcon />}
            label={t('children.dashboard.profile.age')}
            value={`${child.age} ${t('children.dashboard.profile.years_old')}`}
            color="primary"
          />
        </div>

        <div className="col-md-6 col-lg-3">
          <StatCard
            icon={<ZodiacIcon />}
            label={t('children.dashboard.profile.year_of_birth')}
            value={child.year_of_birth}
            color="info"
          />
        </div>

        <div className="col-md-6 col-lg-3">
          <StatCard
            icon={<FireIcon />}
            label={t('children.dashboard.stats.current_streak')}
            value={`${child.current_streak} ${t('children.dashboard.stats.days')}`}
            color="warning"
          />
        </div>

        <div className="col-md-6 col-lg-3">
          <StatCard
            icon={<StarIcon />}
            label={t('children.dashboard.stats.activities_completed')}
            value={stats.completed_sessions_count}
            color="success"
          />
        </div>
      </div>

      {/* Account Info */}
      <div className="card border-0 rounded-4">
        <div className="card-body p-4">
          <h3 className="fs-5 fw-bold mb-3">{t('children.dashboard.profile.account_info')}</h3>
          <div className="row">
            <div className="col-md-6 mb-3">
              <p className="text-secondary small mb-1">{t('children.dashboard.profile.username_label')}</p>
              <p className="fw-medium mb-0">{child.username}</p>
            </div>
            <div className="col-md-6 mb-3">
              <p className="text-secondary small mb-1">{t('children.dashboard.profile.zodiac_animal')}</p>
              <p className="fw-medium mb-0">{child.zodiac_name}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
